import type { FileChangeEventView } from '@/lib/api/generated/model';
import { FILE_EVENT } from '../changeEvents';
import { formatExactTime, formatRelativeTime } from '../format';
import { ViaTool } from '@/features/editor-access';
import { ActorLabel } from './ActorLabel';

/**
 * One recorded change: what happened, who did it, and when.
 *
 * The actor leads because the actor is what anyone scans a change log for —
 * "Modified" in bold was the thing they had already assumed. With the log no
 * longer squeezed into a 16rem rail, all three fit on one line.
 */
export function HistoryEntry({ event }: { event: FileChangeEventView }) {
  const { label, icon: Icon } = FILE_EVENT[event.event_type];

  return (
    <li className="border-border flex items-center gap-2 border-b px-3 py-2 text-sm last:border-b-0">
      <span className="text-muted-foreground flex w-24 shrink-0 items-center gap-1.5 text-xs">
        <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        {label}
      </span>
      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5">
        <ActorLabel actor={event.actor} />
        {/* Done through their AI tool (spec 246); only a person's changes can be. */}
        {event.actor.kind === 'human' && (
          <ViaTool via={event.via} className="text-xs" />
        )}
        {/*
          Where it came from, which is the only thing that makes a move legible
          in a change log — "Moved" on its own says something happened without
          saying what. The service sends `source_path` on exactly these events
          for this purpose; it was being fetched and dropped.
        */}
        {event.source_path !== null && event.source_path !== undefined && (
          <span
            className="text-muted-foreground min-w-0 truncate text-xs"
            title={event.source_path}
          >
            from {event.source_path}
          </span>
        )}
      </span>
      <span
        className="text-muted-foreground shrink-0 text-xs"
        title={formatExactTime(event.occurred_at)}
      >
        {formatRelativeTime(event.occurred_at)}
      </span>
    </li>
  );
}
